import * as R from 'ramda';
import * as Sentry from '@sentry/browser';

import clientInitialState from '../../store/clientInitialState';
import removeClientFromPathname from '../../utils/removeClientFromPathname';
import sportsApi from '../../utils/sportsApi';
import {
    updateUserPreferences,
    applyMergePatch,
    buildAttributionPatch,
    hasLegacyFlatEntries,
    readEdgeAttributionMode,
} from '../../utils/updateUserPreferences';
import { EDGE_RIGHTSHOLDER_ATTRIBUTION } from '../../constants/config';
import i18n from '../../i18n/config';
import { SUPPORTED_LANGUAGES } from '../../i18n/constants';

const selectClient = store => async (state, clientId) => {
    const userId = state.user.id;
    const clients = state.user.clients;

    const id = `${userId}-${clientId}`;

    const selectedClientPermissions = clients.find(
        c => c.id === clientId,
    )?.permissions;
    const hasEdgeRightsholderAttribution = selectedClientPermissions?.includes(
        EDGE_RIGHTSHOLDER_ATTRIBUTION,
    );

    // Fetch user preferences
    let userPreferences = {};
    try {
        const userPreferencesResponse = await sportsApi
            .url(`/users/${userId}/preferences?noClientId=true`)
            .get()
            .json();

        userPreferences = userPreferencesResponse.preferences || {};

        const rightsholderAttribution =
            userPreferences.rightsholder_attribution || {};

        // Whatever mode this client is already in, including one stored under a
        // legacy flat key rather than the edge namespace.
        const existingEdgeAttribution = readEdgeAttributionMode(
            rightsholderAttribution,
            clientId,
        );
        // Legacy flat entries are only reachable through the fallback in that
        // reader, so login is where they get migrated into the namespace. This
        // is the only place every user reliably passes through.
        const needsMigration = hasLegacyFlatEntries(rightsholderAttribution);

        if (
            hasEdgeRightsholderAttribution &&
            (!existingEdgeAttribution || needsMigration)
        ) {
            // Seed the default for a client with no mode; keep the mode of one
            // that has it. Either way, migrate the legacy entries.
            const attributionPatch = buildAttributionPatch(
                rightsholderAttribution,
                clientId,
                existingEdgeAttribution || 'team',
            );
            const locallyPatchedPreferences = {
                ...userPreferences,
                rightsholder_attribution: applyMergePatch(
                    rightsholderAttribution,
                    attributionPatch,
                ),
            };

            // Save the updated preferences back to the API and localStorage
            try {
                userPreferences = await updateUserPreferences({
                    userId,
                    preferences: {
                        rightsholder_attribution: attributionPatch,
                    },
                });
            } catch (updateError) {
                console.error('Error updating user preferences:', updateError);
                // Use the locally updated preferences even if API fails
                userPreferences = locallyPatchedPreferences;
            }
        }
    } catch (error) {
        console.error('Error fetching user preferences:', error);
    }

    // Set i18n language from user preferences if available and supported
    if (
        userPreferences.language &&
        SUPPORTED_LANGUAGES.includes(userPreferences.language)
    ) {
        i18n.changeLanguage(userPreferences.language);
    }

    const newState = R.evolve(
        {
            user: {
                isLoggedIn: R.T,
                isSelectingClient: R.F,
                error: R.always(null),
                selectedClientId: R.always(clientId),
                selectedUserClientId: R.always(id),
                userPreferences: R.always(userPreferences),
            },
        },
        state,
    );

    clients.map(c => {
        const userClientId = `${userId}-${c.id}`;
        newState.clients[userClientId] = R.clone(clientInitialState);
    });

    localStorage.setItem('mvc-user', JSON.stringify(newState.user));
    // TODO: [SC-1492] Fix access for safari on users w/lots of clients
    localStorage.setItem('mvc-user-clients', JSON.stringify(newState.clients));

    const user = newState.user;
    const selectedClient = user.clientsById[clientId];

    removeClientFromPathname(location.pathname);

    if (!__DEV__) {
        Sentry.configureScope(scope => {
            scope.setUser({
                id: user.id,
                email: user.email,
                username: user.username,
                client: selectedClient.name,
                clientId: selectedClient.id,
                roles: user.roles,
            });
        });
    }

    store.setState(newState, true);
};

export default selectClient;
